import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getMyAccountsApi,
  getMyProfileApi,
  switchAccountApi,
  UserAccount,
} from "../apis/authApi";
import { useStoreLogin } from "../state/useAuthStore";

const computeInitials = (
  firstName?: string | null,
  lastName?: string | null,
  email?: string | null
): string => {
  const first = (firstName || "").trim();
  const last = (lastName || "").trim();
  if (first || last) {
    return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase() || "?";
  }
  const emailInitial = (email || "").trim().charAt(0);
  return emailInitial ? emailInitial.toUpperCase() : "?";
};

const computeDisplayName = (
  firstName?: string | null,
  lastName?: string | null,
  email?: string | null
): string => {
  const full = [firstName, lastName]
    .map((part) => (part || "").trim())
    .filter(Boolean)
    .join(" ");
  return full || email || "";
};

export const useProfileMenu = () => {
  const navigate = useNavigate();
  const loginStore = useStoreLogin();
  const queryClient = useQueryClient();

  const { data: profile } = useQuery({
    queryKey: ["myProfile"],
    queryFn: getMyProfileApi,
  });

  const { data: accounts = [] } = useQuery({
    queryKey: ["getMyAccounts"],
    queryFn: getMyAccountsApi,
  });

  const switchMutation = useMutation({
    mutationFn: switchAccountApi,
    onSuccess: async (res) => {
      loginStore(res.accessToken, res.refreshToken);
      // Token is written to storage synchronously and read per-request by the
      // axios interceptor, so a full reload is unnecessary. Reset cached data
      // (scoped to the previous account) and let mounted queries refetch.
      await queryClient.resetQueries();
    },
  });

  const currentAccount = useMemo(
    () => accounts.find((account: UserAccount) => account.isCurrent),
    [accounts]
  );

  const otherAccounts = useMemo(
    () => accounts.filter((account: UserAccount) => !account.isCurrent),
    [accounts]
  );

  const initials = computeInitials(
    profile?.firstName,
    profile?.lastName,
    profile?.email
  );
  const displayName = computeDisplayName(
    profile?.firstName,
    profile?.lastName,
    profile?.email
  );
  const roles = currentAccount?.roles ?? [];
  const isAdmin = roles.some((role) => role.toLowerCase() === "admin");

  const switchAccount = (accountId: number) =>
    switchMutation.mutate({ accountId });

  const signOut = () => navigate("/auth/logout");

  return {
    profile,
    accounts,
    currentAccount,
    otherAccounts,
    hasMultipleAccounts: accounts.length > 1,
    initials,
    displayName,
    email: profile?.email ?? "",
    roles,
    isAdmin,
    switchAccount,
    isSwitching: switchMutation.isPending,
    switchFailed: switchMutation.isError,
    signOut,
  };
};
